import { Data, ResolveFn, Route } from '@angular/router';

/** Route `data` key the top navbar reads to show the "?" button for the page. */
export const HELP_ROUTE_DATA_KEY = 'help';
/** Optional second key: the article an administrator gets on a page both sides use. */
export const HELP_ADMIN_ROUTE_DATA_KEY = 'helpAdmin';

/** `<section>/<slug>` of an article in `help.manifest.ts`. */
export type HelpArticlePath = `${string}/${string}`;

/**
 * Context help for a page: put `data: helpRouteData('bookinger/kalenderen')` on its route, and the
 * top navbar shows a "?" that opens that article. Spread it when the route has other data.
 *
 * A page that both associations and administrators use (Min forening) names the association
 * article first and the administrators' article second; the navbar picks by who is signed in.
 */
export function helpRouteData(article: HelpArticlePath, adminArticle?: HelpArticlePath): Data {
  return adminArticle
    ? { [HELP_ROUTE_DATA_KEY]: article, [HELP_ADMIN_ROUTE_DATA_KEY]: adminArticle }
    : { [HELP_ROUTE_DATA_KEY]: article };
}

/**
 * Context help that depends on state the route cannot know up front — the checkout, whose steps
 * and receipt differ between a booking and an event request. Spread it onto the route instead of
 * `data: helpRouteData(...)`: `...helpRouteResolve(checkoutHelpResolver)`. The resolver runs each
 * time the route is opened, and the resolved article ends up in the same route data the navbar
 * reads. It is not asked again while the page stays open, so the state it reads has to be settled
 * before the page is reached. Call this once per route (a route's `resolve` is written to), and do
 * not give a route both this and `helpRouteData`: the resolved value would win.
 */
export function helpRouteResolve(resolver: ResolveFn<HelpArticlePath>): Pick<Route, 'resolve'> {
  return { resolve: { [HELP_ROUTE_DATA_KEY]: resolver } };
}

/**
 * The article a page's "?" opens for this reader, from the route's `data`: the association
 * article for an organisation's person, otherwise the administrators' one when the page names
 * both, otherwise the single article.
 * @param data The leaf route's resolved `data`
 * @param association Whether the reader is an organisation's person (`isAssociationLogin`)
 * @returns `<section>/<slug>`, or `null` when the page has no help
 */
export function helpArticleFor(data: Data, association: boolean): HelpArticlePath | null {
  const key = association ? HELP_ROUTE_DATA_KEY : HELP_ADMIN_ROUTE_DATA_KEY;
  return data[key] ?? data[HELP_ROUTE_DATA_KEY] ?? null;
}
